export type OwnerPhotoPreview = { url: string; expiresAt: string } | null | undefined;

export function ownerPhotoPreviewSrc(
  preview: OwnerPhotoPreview,
  allowLocalHttp = false,
): string | null {
  if (!preview) return null;
  try {
    if (preview.url.startsWith('/api/v1/media/')) {
      const url = new URL(preview.url, 'https://ponchik.invalid');
      if (
        url.origin === 'https://ponchik.invalid' &&
        /^\/api\/v1\/media\/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\/content$/i.test(
          url.pathname,
        ) &&
        url.searchParams.size === 2 &&
        /^\d{10}$/.test(url.searchParams.get('expires') ?? '') &&
        /^[a-f0-9]{64}$/.test(url.searchParams.get('signature') ?? '') &&
        !url.hash
      )
        return url.pathname + url.search;
      return null;
    }
    const url = new URL(preview.url);
    const localHttp =
      allowLocalHttp &&
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1', '::1'].includes(url.hostname);
    return url.protocol === 'https:' || localHttp ? preview.url : null;
  } catch {
    return null;
  }
}

export function canRemoveOwnerPhoto(status: string): boolean {
  return status !== 'DELETED';
}

export const MAX_PROFILE_PHOTOS = 3;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const PHOTO_MAX_EDGE = 1920;
export const PHOTO_MIN_EDGE = 320;
export const CANONICAL_PHOTO_MIME = 'image/jpeg';

export function terminalPhotoCollectionStatus(
  pendingStates: readonly string[],
  persistedStates: readonly string[],
): 'READY' | 'FAILED' | null {
  if (pendingStates.includes('FAILED') || persistedStates.includes('FAILED')) return 'FAILED';
  if (
    pendingStates.length === 0 &&
    persistedStates.includes('READY') &&
    !persistedStates.some((status) => ['UPLOADING', 'UPLOADED', 'PROCESSING'].includes(status))
  )
    return 'READY';
  return null;
}

export function terminalPhotoStatusMessage(
  current: string,
  terminal: ReturnType<typeof terminalPhotoCollectionStatus>,
): string {
  if (current === 'Эта фотография уже добавлена') return current;
  if (terminal === 'FAILED') return 'Не удалось обработать фото. Повторите попытку.';
  if (terminal === 'READY') return 'Фотографии готовы.';
  return current;
}

export function scaledPhotoDimensions(width: number, height: number) {
  const scale = Math.min(
    PHOTO_MAX_EDGE / Math.max(width, height),
    Math.max(1, PHOTO_MIN_EDGE / Math.min(width, height)),
  );
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function isDuplicatePhotoError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'DUPLICATE_PHOTO'
  );
}

export function isProgressiveJpegWithoutJfif(bytes: Uint8Array): boolean {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return false;
  let offset = 2;
  let progressive = false;
  let jfif = false;
  while (offset + 1 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1;
    const marker = bytes[offset++];
    if (marker === undefined || marker === 0xd9 || marker === 0xda) break;
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (offset + 1 >= bytes.length) break;
    const length = (bytes[offset]! << 8) | bytes[offset + 1]!;
    if (length < 2 || offset + length > bytes.length) break;
    const payload = offset + 2;
    if (
      marker === 0xe0 &&
      bytes[payload] === 0x4a &&
      bytes[payload + 1] === 0x46 &&
      bytes[payload + 2] === 0x49 &&
      bytes[payload + 3] === 0x46 &&
      bytes[payload + 4] === 0
    )
      jfif = true;
    if (marker === 0xc2) progressive = true;
    offset += length;
  }
  return progressive && !jfif;
}

export async function withPhotoDecodeDeadline<T>(
  operation: Promise<T>,
  timeoutMs = 3_000,
): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error('PHOTO_BROWSER_DECODE_TIMEOUT')), timeoutMs);
      }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

async function decodeProfilePhoto(file: File): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}> {
  try {
    const bitmap = await withPhotoDecodeDeadline(
      createImageBitmap(file, { imageOrientation: 'from-image' }),
    );
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
    };
  } catch (bitmapError) {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    try {
      await withPhotoDecodeDeadline(image.decode());
      if (!image.naturalWidth || !image.naturalHeight) throw bitmapError;
      return {
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        close: () => URL.revokeObjectURL(url),
      };
    } catch (imageError) {
      URL.revokeObjectURL(url);
      throw imageError;
    }
  }
}

export async function runPhotoUploadQueue<T>(
  items: readonly T[],
  upload: (item: T) => Promise<void>,
): Promise<void> {
  for (const item of items) await upload(item);
}

export function normalizePhotoName(value: string): string {
  return value.replace(/\.[^.]+$/u, '') || 'photo';
}

/** Browser-side optimisation is UX-only; the API/worker remains the validation authority. */
export async function optimizeProfilePhoto(file: File): Promise<File> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type))
    throw new Error('UNSUPPORTED_PROFILE_PHOTO');
  // Chromium can block synchronously before createImageBitmap returns a Promise
  // for this valid marker combination. Bypass the browser decoder so the
  // authoritative worker can quarantine, fully decode and canonicalize it.
  if (
    file.type === 'image/jpeg' &&
    isProgressiveJpegWithoutJfif(new Uint8Array(await file.arrayBuffer()))
  )
    return file;
  const decoded = await decodeProfilePhoto(file).catch(() => null);
  // Some structurally valid progressive JPEGs are readable by the authoritative
  // Sharp decoder but not by Chromium's bitmap or <img> decoder. Preserve the
  // original for quarantine processing instead of classifying it as corrupt.
  if (!decoded) return file;
  try {
    const canvas = document.createElement('canvas');
    const dimensions = scaledPhotoDimensions(decoded.width, decoded.height);
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('PHOTO_CANVAS_UNAVAILABLE');
    context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, CANONICAL_PHOTO_MIME, 0.88),
    );
    if (!blob) throw new Error('PHOTO_CANONICAL_ENCODING_FAILED');
    return new File([blob], `${normalizePhotoName(file.name)}.jpg`, {
      type: CANONICAL_PHOTO_MIME,
      lastModified: file.lastModified,
    });
  } finally {
    decoded.close();
  }
}

export async function profilePhotoDimensions(file: File): Promise<{
  width: number;
  height: number;
}> {
  if (
    file.type === 'image/jpeg' &&
    isProgressiveJpegWithoutJfif(new Uint8Array(await file.arrayBuffer()))
  )
    return { width: 0, height: 0 };
  const decoded = await decodeProfilePhoto(file).catch(() => null);
  if (!decoded) return { width: 0, height: 0 };
  try {
    return { width: decoded.width, height: decoded.height };
  } finally {
    decoded.close();
  }
}
